import shortid from 'shortid';
import { toNumber } from '../../utils/numbers';
import { RunMode } from '../../constants/Configuration';
import { IBatchData } from './IBatchData';

class BatchData implements IBatchData {
  uid: string;
  id: string;
  _id: string;
  loadStartTime: number | null;
  loadStartTimeDiff: number;
  loadEndTime: number | string | null;
  isDemo: boolean;

  dispatchTicketCode: string | null;
  dispatchOrderCode: string | null;
  batchOrderCode: string | null;
  code: string | null;

  printWithoutWeights: boolean;
  printWithoutWeightsAtEnd: boolean;
  printWithWeights: boolean;
  printOnlyWeights: boolean;
  batchStartedOffline: boolean;

  batchStartedRemotely: boolean;
  batchOrderId: any;
  ticket: any;
  tId: string;
  order: any;
  orderCode: any;
  mix: any;
  mixId: string | number | null;
  mixInTruck: string | null;
  products: any[];
  mixDesign: any[] | null;
  mixDesignCode: string | null;
  itemsToBeManuallyAdded: any[];

  secondWater: any;
  batchSize: any;
  allDrops: number;
  dropSize: number;
  currentDrop: number;
  balance: number;

  materialId: any;
  quantity: any;
  quantityToBeBatched: any;
  material: any;
  binPercent: Record<string, any>;
  isWashout: boolean;
  mixQuantityInTruck: number;
  heatChargeAmt: number;
  heatChargePrt: string;
  minimumLoadChargeAmt: number;
  minimumLoadChargePrt: string;

  scaleSize: number;
  isOverride: boolean;
  scales: any;
  pumps: any;
  finalScales: any;
  finalScalesDischarged: any[];
  mixerId: number;

  waterUnit: string;
  waterToReduce: number;
  headWater: number;
  headWaterUnconverted: number;
  headWaterPumpDischarged: boolean;
  headWaterTankDischarged: boolean;
  headWaterScaleDischarged: boolean;
  washWater: number;
  washWaterUnconverted: number;
  washWaterPumpDischarged: boolean;
  washWaterTankDischarged: boolean;
  startWashWaterManually: boolean;

  sequence: any;
  dischargeSequenceId: any;
  dischargeSequenceName: string;
  results: any[];
  batchDrops: Record<string, any>;
  drops: any[];
  dropTimes: any[];
  pumpPrecisionLoss: Record<string, any>;

  manualLoad: boolean;
  temperWaterStart: number;
  temperWaterFinish: number;
  temperWater: number;
  temperWaterUnitSymbol: any;
  temperWaterUnitId: any;
  temperWaterPNo: any;
  adjustedWater: number;
  batchActionFired: any[];

  useBay: boolean;
  bay: number;
  truckId: string;
  truckCode: string;
  scalesFinishedDischargeEarlier: any[];

  isHoldLoad: boolean;
  mixingStartTime: number | null;
  mixerFeedingStartTime: number | null;
  mixingTime: number;
  mergeBatchTicketId: any;
  isTestMode: boolean;
  runMode: string;
  lotBlockNumber: string;
  dischargeAuthorization: boolean;
  orderedBy: any;
  onJobDueTime: any;
  totalQuantityTax: any;
  totalQuantityPrice: any;
  resultsSent: boolean;
  resultsSending: boolean;
  remoteUser: any;
  waterToAdjust: number;
  autoMoistureDeduction: number;
  isMeteredWaterInTheMix: boolean;
  isWeighedWaterInTheMix: boolean;
  events: any[];
  verification: any|null;
  isBatchReadyToFinish: boolean;
  isAborted: boolean;
  originalPayload: any;

  constructor(data: Partial<IBatchData> = {}) {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        (this as Record<string, unknown>)[key] = data[key as keyof IBatchData];
      }
    }

    this.uid = data.uid || shortid.generate();
    this.id = data.id || shortid.generate();
    this._id = data._id || shortid.generate();

    this.loadStartTime = data.loadStartTime || null;
    this.loadStartTimeDiff = data.loadStartTimeDiff || 0;
    this.loadEndTime = data.loadEndTime || null;
    this.isDemo = data.isDemo || false;

    this.dispatchTicketCode = data.dispatchTicketCode || null;
    this.dispatchOrderCode = data.dispatchOrderCode || null;
    this.batchOrderCode = data.batchOrderCode || null;
    this.code = data.code || null;

    this.printWithoutWeights = !!data.printWithoutWeights;
    this.printWithoutWeightsAtEnd = !!data.printWithoutWeightsAtEnd;
    this.printWithWeights = !!data.printWithWeights;
    this.printOnlyWeights = !!data.printOnlyWeights;
    this.mixId = data.mixId || null;
    this.mixInTruck = data.mixInTruck || null;
    this.products = data.products || [];

    if (data.order?.products) {
      data.mix = data.order.products.find((v: any) => v.isMix);
    } else if (data.mix) {
      data.order = {};
    }

    this.batchStartedOffline = data.batchStartedOffline || false;
    this.batchStartedRemotely = data.batchStartedRemotely || false;
    this.batchOrderId = data.batchOrderId;
    this.ticket = data.ticket;
    this.tId = data.dispatchTicketCode || (data.ticket ? data.ticket.code || data.ticket.id : data.id) || this.id || this.uid || '';
    this.order = data.order;
    this.orderCode = data.dispatchOrderCode || data.batchOrderCode || data.code;
    this.mix = data.mix;
    this.mixDesignCode = data.mix?.itemCode || null;
    this.mixDesign = data.mixDesign ? data.mixDesign.map((v: any) => ({ ...v })).sort((a, b) => a.order - b.order) : null;
    this.itemsToBeManuallyAdded = data.itemsToBeManuallyAdded || [];

    this.secondWater = data.secondWater;
    this.batchSize = data.batchSize;
    this.allDrops = toNumber(data.allDrops);
    // @ts-ignore
    this.dropSize = toNumber(data.batchSize / data.allDrops, 2);
    this.currentDrop = data.currentDrop || 1;
    // @ts-ignore
    this.balance = data.currentDrop > 1 ? toNumber(data.batchSize - (data.dropSize * (data.currentDrop - 1)), 2) : data.batchSize;

    this.materialId = data.materialId;
    this.quantity = data.quantity;
    this.material = data.material;
    this.binPercent = data.binPercent || {};
    this.isWashout = data.isWashout || false;
    this.mixQuantityInTruck = data.mixQuantityInTruck || 0;
    this.heatChargeAmt = data.heatChargeAmt || 0;
    this.heatChargePrt = data.heatChargePrt || '';
    this.minimumLoadChargeAmt = data.minimumLoadChargeAmt || 0;
    this.minimumLoadChargePrt = data.minimumLoadChargePrt || '';

    this.scaleSize = data.scaleSize || 0;
    this.isOverride = data.isOverride || false;
    this.scales = data.scales;
    this.pumps = data.pumps;
    this.finalScales = data.finalScales;
    this.finalScalesDischarged = data.finalScalesDischarged || [];
    this.mixerId = Number(data.mixerId);

    this.waterUnit = data.waterUnit || '';
    this.waterToReduce = data.waterToReduce || 0;
    this.headWater = data.headWater || 0;
    this.headWaterUnconverted = data.headWaterUnconverted || 0;
    this.headWaterPumpDischarged = data.headWaterPumpDischarged || false;
    this.headWaterTankDischarged = data.headWaterTankDischarged || false;
    this.headWaterScaleDischarged = data.headWaterScaleDischarged || false;
    this.washWater = data.washWater || 0;
    this.washWaterUnconverted = data.washWaterUnconverted || 0;
    this.washWaterPumpDischarged = data.washWaterPumpDischarged || false;
    this.washWaterTankDischarged = data.washWaterTankDischarged || false;
    this.startWashWaterManually = data.startWashWaterManually || false;
    this.sequence = data.sequence;
    this.dischargeSequenceId = this.sequence ? this.sequence.id : null;
    this.dischargeSequenceName = this.sequence ? this.sequence.name : '';
    this.results = data.results || [];
    this.batchDrops = {};
    this.drops = [];
    this.dropTimes = [];
    this.pumpPrecisionLoss = data.pumpPrecisionLoss || {}; // Object to store per-batch accumulated loss
    this.manualLoad = !!data.manualLoad;
    this.temperWaterStart = data.temperWaterStart || 0;
    this.temperWaterFinish = data.temperWaterFinish || 0;
    this.temperWater = data.temperWater || 0;
    this.temperWaterUnitSymbol = data.temperWaterUnitSymbol;
    this.temperWaterUnitId = data.temperWaterUnitId;
    this.temperWaterPNo = data.temperWaterPNo;
    this.adjustedWater = data.adjustedWater || 0;
    this.batchActionFired = [];
    this.useBay = data.useBay || false;
    this.bay = data.bay ? toNumber(data.bay) : 0;
    this.truckId = data.truckId || '';
    this.truckCode = data.truckCode || '';
    this.scalesFinishedDischargeEarlier = [];
    this.isHoldLoad = data.isHoldLoad || false;
    this.mixingStartTime = data.mixingStartTime || null;
    this.mixerFeedingStartTime = data.mixerFeedingStartTime || null;
    this.mixingTime = data.mixingTime || 0;
    this.mergeBatchTicketId = data.mergeBatchTicketId || null;
    this.isDemo = !!data.isDemo;
    this.isTestMode = !!data.isTestMode;
    this.runMode = data.runMode || RunMode.Normal;
    this.lotBlockNumber = data.lotBlockNumber || '';
    this.dischargeAuthorization = data.dischargeAuthorization || false;
    this.orderedBy = data.orderedBy;
    this.onJobDueTime = data.onJobDueTime;
    this.totalQuantityTax = data.totalQuantityTax;
    this.totalQuantityPrice = data.totalQuantityPrice;
    this.resultsSent = data.resultsSent || false;
    this.resultsSending = data.resultsSending || false;
    this.remoteUser = data.remoteUser;
    this.waterToAdjust = data.waterToAdjust || 0;
    this.autoMoistureDeduction = data.autoMoistureDeduction || 0;
    this.isMeteredWaterInTheMix = data.isMeteredWaterInTheMix || false;
    this.isWeighedWaterInTheMix = data.isWeighedWaterInTheMix || false;
    this.events = data.events || [];
    this.verification = data.verification || null;
    this.isBatchReadyToFinish = data.isBatchReadyToFinish || false;
    this.isAborted = !!data.isAborted;
  }
}

export default BatchData;
